"use client";
import { Globe2 } from "lucide-react";
import PageContent from "@/components/page-content";
import SectionHeader from "@/components/landing/section-header";
import { cn } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";


type Status = "best" | "neutral";
type ModelStat = { value: string; status: Status };
type ComparisonRow = {
  metric: string;
  moon: ModelStat;
  gpt5: ModelStat;
  gemini: ModelStat;
  claude: ModelStat;
};

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const MODEL_COL_WIDTH = "w-20 md:w-32 lg:w-40";

const Compare = () => {

  const comparisonData: ComparisonRow[] = [
    {
      metric: "RefCOCO",
      moon: { value: "91.1", status: "best" },
      gpt5: { value: "57.2", status: "neutral" },
      gemini: { value: "75.8", status: "neutral" },
      claude: { value: "30.1", status: "neutral" },
    },
    {
      metric: "RefCOCOg",
      moon: { value: "88.6", status: "best" },
      gpt5: { value: "49.8", status: "neutral" },
      gemini: { value: "75.1", status: "neutral" },
      claude: { value: "26.2", status: "neutral" },
    },
    {
      metric: "RefCOCO+",
      moon: { value: "81.8", status: "best" },
      gpt5: { value: "46.3", status: "neutral" },
      gemini: { value: "70.2", status: "neutral" },
      claude: { value: "23.4", status: "neutral" },
    },
  ]

  function ModelTableCell({
    row,
    model,
  }: {
    row: ComparisonRow;
    model: keyof Omit<ComparisonRow, "metric">;
  }) {
    return (
      <TableCell
        className={cn(
          "py-4 text-muted-foreground text-center font-medium",
          MODEL_COL_WIDTH,
          model === "moon" && "bg-purple-200/30 dark:bg-purple-950/20",
          row[model].status === "best" && model === "moon" && "font-bold text-foreground",
          row[model].status === "best" && model !== "moon" && "text-foreground",
        )}
      >
        <div className="flex items-center justify-center gap-2">
          <span>{row[model].value}</span>
        </div>
      </TableCell>
    )
  }

  return (
    <PageContent>
      <SectionHeader
        sectionIcon={<Globe2 className="size-4" />}
        section="Benchmarks"
        heading="Fast, accurate object detection"
        description="Moondream 3 achieves the highest scores on standard grounding benchmarks while being faster and more cost-effective."
      />

      <div>
        <div className="relative overflow-hidden p-4 md:p-6">
          <div className="border-border/50 bg-background/50 relative overflow-x-auto border backdrop-blur-sm">
            <Table className="w-full min-w-[500px]">
              <TableHeader>
                <TableRow className="border-border/50 bg-muted/50">
                  <TableHead className="text-foreground font-semibold">
                  </TableHead>
                  <TableHead
                    className={cn(
                      "text-foreground text-center font-semibold bg-purple-200/50 dark:bg-purple-950/40 px-3 py-3",
                      MODEL_COL_WIDTH,
                    )}
                  >
                    <div className="flex items-center justify-center gap-2">
                      Moondream
                    </div>
                  </TableHead>
                  <TableHead
                    className={cn(
                      "text-foreground text-center font-semibold px-3 py-3",
                      MODEL_COL_WIDTH,
                    )}
                  >
                    <div className="flex items-center justify-center gap-2">
                      GPT-5
                    </div>
                  </TableHead>
                  <TableHead
                    className={cn(
                      "text-foreground text-center font-semibold px-3 py-3",
                      MODEL_COL_WIDTH,
                    )}
                  >
                    <div className="flex items-center justify-center gap-2">
                      Gemini 2.5 Flash
                    </div>
                  </TableHead>
                  <TableHead
                    className={cn(
                      "text-foreground text-center font-semibold px-3 py-3",
                      MODEL_COL_WIDTH,
                    )}
                  >
                    <div className="flex items-center justify-center gap-2">
                      Claude 4 Sonnet
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonData.map((row, index) => (
                  <TableRow
                    key={index}
                    className={cn(
                      "border-border/30 hover:bg-muted/30 transition-colors",
                      index % 2 === 1 && "bg-muted/20"
                    )}
                  >
                    <TableCell className="text-foreground py-4 font-medium">
                      {row.metric}
                    </TableCell>
                    <ModelTableCell row={row} model="moon" />
                    <ModelTableCell row={row} model="gpt5" />
                    <ModelTableCell row={row} model="gemini" />
                    <ModelTableCell row={row} model="claude" />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Methodology Section */}
          <Accordion type="single" collapsible className="mt-6">
            <AccordionItem value="methodology" className="border-border/50">
              <AccordionTrigger className="text-muted-foreground hover:text-foreground text-sm hover:no-underline">
                Methodology &amp; Notes
              </AccordionTrigger>
              <AccordionContent>
                <ul className="text-muted-foreground space-y-3 text-sm list-disc pl-5">
                  <li>
                    <strong className="text-foreground">RefCOCO, RefCOCOg, RefCOCO+</strong> are standard benchmarks for evaluating referring expression comprehension and object grounding capabilities. Scores represent accuracy in detecting objects from natural language descriptions.
                  </li>
                  <li>
                    <strong className="text-foreground">Moondream 3</strong> uses dedicated grounding tokens (3 tokens per bounding box) making it significantly more token-efficient than text-based approaches.
                  </li>
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </PageContent >
  );
};

export { Compare };

